import { useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const vertexShader = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec3 vWorldPosition;
  varying vec2 vUv;

  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    vNormal = normalMatrix * normal;
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    vUv = uv;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uLightPos;
  uniform vec3 uLightPos2;
  uniform float uTime;

  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec3 vWorldPosition;
  varying vec2 vUv;

  float hash(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  float ggx(float NdotH, float roughness) {
    float a = roughness * roughness;
    float a2 = a * a;
    float d = NdotH * NdotH * (a2 - 1.0) + 1.0;
    return a2 / (3.14159 * d * d);
  }

  void main() {
    vec3 N = normalize(vNormal);
    vec3 V = normalize(vViewPosition);
    vec3 R = reflect(-V, N);

    // Fake HDR environment via reflection direction — this is what
    // makes metal read as metal instead of plastic
    float ry = R.y * 0.5 + 0.5;
    vec3 envSky  = vec3(0.25, 0.27, 0.34);
    vec3 envGnd  = vec3(0.01, 0.01, 0.02);
    vec3 env = mix(envGnd, envSky, smoothstep(0.0, 1.0, ry));
    float horizon = exp(-abs(R.y) * 3.5);
    env += vec3(0.4, 0.4, 0.45) * horizon * 0.4;

    // Schlick Fresnel with high F0 (metals ≈ 0.6-0.9)
    float NdotV = max(dot(N, V), 0.0);
    vec3 F0 = uColor * 0.75;
    vec3 fresnel = F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0);

    // Metallic environment reflection (tinted by surface color)
    vec3 color = env * fresnel;

    // GGX specular from primary light — higher roughness = wider, softer spread
    float roughness = 0.28;
    vec3 L1 = normalize(uLightPos - vWorldPosition);
    vec3 H1 = normalize(L1 + V);
    float D1 = ggx(max(dot(N, H1), 0.0), roughness);
    float NdotL1 = max(dot(N, L1), 0.0);
    float dist1 = length(uLightPos - vWorldPosition);
    float atten1 = 1.0 / (1.0 + 0.025 * dist1 * dist1);
    color += uColor * D1 * NdotL1 * atten1 * 0.35;

    float Dsharp = ggx(max(dot(N, H1), 0.0), 0.10);
    color += vec3(1.0) * Dsharp * NdotL1 * atten1 * 0.03;

    // GGX specular from secondary light
    vec3 L2 = normalize(uLightPos2 - vWorldPosition);
    vec3 H2 = normalize(L2 + V);
    float D2 = ggx(max(dot(N, H2), 0.0), roughness);
    float NdotL2 = max(dot(N, L2), 0.0);
    float dist2 = length(uLightPos2 - vWorldPosition);
    float atten2 = 1.0 / (1.0 + 0.04 * dist2 * dist2);
    color += uColor * D2 * NdotL2 * atten2 * 0.15;

    // Subtle grain for polished metal texture
    vec2 screenUV = gl_FragCoord.xy;
    float grain = hash(screenUV + uTime * 0.07) * 0.018 - 0.009;
    color += grain;

    gl_FragColor = vec4(color, 1.0);
  }
`;

function DustField() {
  const count = 120;
  const meshRef = useRef<THREE.Points>(null);

  const [positions, sizes] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 16;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 8;
      sz[i] = Math.random() * 1.5 + 0.5;
    }
    return [pos, sz];
  }, []);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();
    const posArr = meshRef.current.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < count; i++) {
      posArr[i * 3 + 1] += Math.sin(t * 0.2 + i) * 0.0008;
      posArr[i * 3] += Math.cos(t * 0.15 + i * 0.5) * 0.0005;
    }
    meshRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-size" args={[sizes, 1]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.015}
        color="#ffffff"
        transparent
        opacity={0.12}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

interface ScrollRef {
  current: number;
}

function LeverSculpture({
  mouse,
  scroll,
}: {
  mouse: React.RefObject<THREE.Vector2 | null>;
  scroll: React.RefObject<ScrollRef>;
}) {
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const barRef = useRef<THREE.Mesh>(null);
  const matBarRef = useRef<THREE.ShaderMaterial>(null);
  const matCylRef = useRef<THREE.ShaderMaterial>(null);
  const lightPos = useRef(new THREE.Vector3(4, 3, 6));
  const lightPos2 = useRef(new THREE.Vector3(-5, -2, 3));
  const _unproj = useRef(new THREE.Vector3());
  const _rayDir = useRef(new THREE.Vector3());

  const barLength = 6.2;
  const barThickness = 0.75;
  const cylRadius = 0.85;
  const cylHeight = 0.6;
  const barDepth = cylHeight;
  const baseAngle = Math.PI / 5.2;
  const tangentDist = cylRadius + barThickness / 2;
  const scrollSwing = Math.PI * 0.42;

  const currentAngle = useRef(baseAngle);

  const initBarX = -tangentDist * Math.sin(baseAngle);
  const initBarY = tangentDist * Math.cos(baseAngle);

  const uniforms = useMemo(
    () => ({
      bar: {
        uColor: { value: new THREE.Color(0.72, 0.73, 0.78) },
        uLightPos: { value: new THREE.Vector3(4, 3, 6) },
        uLightPos2: { value: new THREE.Vector3(-5, -2, 3) },
        uTime: { value: 0 },
      },
      cyl: {
        uColor: { value: new THREE.Color(0.80, 0.81, 0.86) },
        uLightPos: { value: new THREE.Vector3(4, 3, 6) },
        uLightPos2: { value: new THREE.Vector3(-5, -2, 3) },
        uTime: { value: 0 },
      },
    }),
    [],
  );

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const m = mouse.current;
    const mx = m ? m.x : 0;
    const my = m ? m.y : 0;
    const s = scroll.current?.current ?? 0;

    // Bar angle tracks scroll with smooth interpolation
    const targetAngle = baseAngle - s * scrollSwing;
    currentAngle.current += (targetAngle - currentAngle.current) * 0.05;
    const theta = currentAngle.current;

    // Place bar on the tangent circle around the cylinder so it
    // always rests on the surface without intersecting
    if (barRef.current) {
      barRef.current.position.x = -tangentDist * Math.sin(theta);
      barRef.current.position.y = tangentDist * Math.cos(theta);
      barRef.current.rotation.z = theta;
    }

    if (groupRef.current) {
      groupRef.current.rotation.y += (Math.sin(t * 0.12) * 0.18 + mx * 0.15 - groupRef.current.rotation.y) * 0.02;
      groupRef.current.rotation.x += (Math.cos(t * 0.08) * 0.06 + my * 0.08 - 0.05 - groupRef.current.rotation.x) * 0.02;

      const breathe = 1 + Math.sin(t * 0.6) * 0.01;
      groupRef.current.scale.setScalar(breathe);
    }

    // Unproject cursor NDC into a world-space ray, intersect with z=0
    // to find where the cursor "points" on the object plane
    _unproj.current.set(mx, my, 0.5).unproject(camera);
    _rayDir.current.copy(_unproj.current).sub(camera.position).normalize();
    const rayT = -camera.position.z / _rayDir.current.z;
    const hitX = camera.position.x + _rayDir.current.x * rayT;
    const hitY = camera.position.y + _rayDir.current.y * rayT;

    // Primary light: placed at cursor hit, offset toward camera so the
    // GGX highlight appears right where the cursor is on the surface
    lightPos.current.x += (hitX - lightPos.current.x) * 0.15;
    lightPos.current.y += (hitY - lightPos.current.y) * 0.15;
    lightPos.current.z = 2.5;
    // Fill light: fixed opposite side for ambient contrast
    lightPos2.current.set(-3.0, 1.0, 5.0);

    const mats = [matBarRef.current, matCylRef.current];
    for (const mat of mats) {
      if (!mat) continue;
      mat.uniforms.uLightPos.value.copy(lightPos.current);
      mat.uniforms.uLightPos2.value.copy(lightPos2.current);
      mat.uniforms.uTime.value = t;
    }
  });

  return (
    <group ref={groupRef} position={[-initBarX, 0.2 - initBarY, 0]}>
      <mesh
        ref={barRef}
        position={[initBarX, initBarY, 0]}
        rotation={[0, 0, baseAngle]}
      >
        <boxGeometry args={[barLength, barThickness, barDepth, 2, 2, 2]} />
        <shaderMaterial
          ref={matBarRef}
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms.bar}
        />
      </mesh>

      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[cylRadius, cylRadius, cylHeight, 48]} />
        <shaderMaterial
          ref={matCylRef}
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms.cyl}
        />
      </mesh>
    </group>
  );
}

function CameraRig({
  mouse,
  scroll,
}: {
  mouse: React.RefObject<THREE.Vector2 | null>;
  scroll: React.RefObject<ScrollRef>;
}) {
  const { camera } = useThree();
  const target = useRef(new THREE.Vector3(0, 0.3, 6.5));

  useFrame(() => {
    const m = mouse.current;
    const mx = m ? m.x : 0;
    const my = m ? m.y : 0;
    const s = scroll.current?.current ?? 0;

    // Scroll-driven orbit: camera sweeps ~40° around and rises
    const orbitAngle = s * 0.7;
    const baseRadius = 6.5;
    const radius = baseRadius + s * 1.8;
    const height = 0.15 + s * 1.4;

    const orbitX = Math.sin(orbitAngle) * radius;
    const orbitZ = Math.cos(orbitAngle) * radius;

    target.current.set(
      orbitX + mx * 1.2,
      height + my * 0.7,
      orbitZ,
    );

    camera.position.x += (target.current.x - camera.position.x) * 0.025;
    camera.position.y += (target.current.y - camera.position.y) * 0.025;
    camera.position.z += (target.current.z - camera.position.z) * 0.025;
    camera.lookAt(0, 0, 0);
  });

  return null;
}

export function LeverScene({ scrollRef }: { scrollRef?: React.RefObject<ScrollRef> }) {
  const mouse = useRef<THREE.Vector2>(new THREE.Vector2(0, 0));
  const fallbackScroll = useRef<ScrollRef>({ current: 0 });
  const scroll = scrollRef ?? fallbackScroll;

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouse.current.set(
        (e.clientX / window.innerWidth) * 2 - 1,
        -(e.clientY / window.innerHeight) * 2 + 1,
      );
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 0 }}>
      <Canvas
        camera={{ position: [0, 0.15, 6.5], fov: 32 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: false, powerPreference: "high-performance" }}
        onCreated={({ gl }) => {
          gl.setClearColor("#000000");
          gl.toneMapping = THREE.NoToneMapping;
        }}
      >
        <ambientLight intensity={0.04} />
        <LeverSculpture mouse={mouse} scroll={scroll} />
        <DustField />
        <CameraRig mouse={mouse} scroll={scroll} />
      </Canvas>
    </div>
  );
}
